/**
 * Product Gallery Carousel
 */

import Flickity from 'flickity';
import postArtwork from './post-artwork';

function productGalleryCarousel(opts) {
  const config = {
    dom: {
      gallery: '.js-product-gallery',
      carousel: 'js-product-gallery-carousel',
      post_artwork: 'js-post-artwork-image',
      post_artwork_wrapper: '.js-post-artwork-image',
      post_artwork_cell: '.js-uploaded-artwork',
      videoBtn: '.js-video-control',
    },
    flickityOpts: {
      prevNextButtons: false,
      pageDots: false,
      arrowShape: '',
      watchCSS: true, // mobile only
      wrapAround: true,
      cellSelector: '.js-product-gallery-carousel-cell',
    },
    cls: {
      selected: '.is-selected',
      navButton: 'js-product-gallery-nav-button',
      navButtonActive: 'is-active',
    },
  };

  const c = __.extend(config, opts);
  const $carousels = [...document.querySelectorAll(`.${c.dom.carousel}`)];

  /**
   * Init
   */
  function init() {
    if ($carousels && $carousels.length > 0) {
      $carousels.forEach(($carousel, idx) => {
        const $gallery = __.parents($carousel, c.dom.gallery)[0] || false;

        if (idx === 0 && $gallery) {
          arrangeImages($carousel, $gallery);
        } else {
          arrangeImages($carousel);
        }
      });
    }
  }

  function arrangeImages(carousel, gallery = false) {
    if (!carousel) return;
    const $postArtworkCells = carousel.querySelectorAll(c.dom.post_artwork_cell) || [];

    if ($postArtworkCells?.length > 0) {
      [...$postArtworkCells].forEach(($cell) => {
        if ($cell !== $postArtworkCells[0]) {
          $cell?.remove();
        } else {
          checkFrameSpecData($cell);
        }
      });
    }

    const $productImages = [...carousel.querySelectorAll(c.flickityOpts.cellSelector)];

    const $productImagesSorted = $productImages.sort((a, b) => {
      const aPriority = a.dataset.imagePriority;
      const bPriority = b.dataset.imagePriority;
      return aPriority - bPriority;
    });

    if ($productImagesSorted?.length > 0) {
      carousel.innerHTML = '';
      [...$productImagesSorted].forEach(($productImage, idx) => {
        $productImage.setAttribute('data-cell-index', idx);

        carousel.appendChild($productImage);
      });

      if (gallery) {
        __.removeClass(gallery, 'hide');
      }

      makeCarousel(carousel);
    } else {
      if (gallery) {
        __.removeClass(gallery, 'hide');
      }

      makeCarousel(carousel);
    }
  }

  /**
   * Check Frame Spec Data
   * @description checks if there is a current spec and initiates post artwork
   * @param {element} $cell
   * @returns {void}
   */
  function checkFrameSpecData($cell) {
    const { variantSpec } = $cell.dataset;
    const uploadedArtwork = window.localStorage.getItem('uploadedArtwork');
    const hasUploadedArtwork = Boolean(uploadedArtwork);

    if (!variantSpec && !hasUploadedArtwork) {
      // No variant spec and no explicit upload — remove the post-artwork cell.
      // Framing-flow artwork (currentFrameSpec) is intentionally not honored here.
      const $wrappers = [...document.querySelectorAll(c.dom.post_artwork_wrapper)];
      $wrappers.forEach(($wrapper) => {
        const $carouselCell = $wrapper?.parentNode || null;

        if ($carouselCell) {
          $carouselCell.remove();
        } else if (!$carouselCell && $wrapper) {
          $wrapper.remove();
        }
      });

      return;
    }

    postArtwork();
  }

  /**
   * Get image source from cell
   */
  function getImageFromCell($cell) {
    const $img = $cell.querySelector('img');
    if ($img) {
      const imgSrc = $img.getAttribute('data-src') || $img.getAttribute('data-srcset') || $img.src || $img.getAttribute('src');
      if (imgSrc) {
        let thumbnailSrc = imgSrc;
        if (imgSrc.includes('_{width}x')) {
          thumbnailSrc = imgSrc.replace('_{width}x', '_120x');
        } else if (imgSrc.includes('image_url')) {
          // get smaller version of image
          thumbnailSrc = imgSrc.replace(/\/(\d+)x(\d+)\//, '/120x120/');
        }

        return {
          src: thumbnailSrc,
          srcset: $img.getAttribute('data-srcset') || $img.srcset || $img.getAttribute('srcset') || '',
          alt: $img.alt || $img.getAttribute('alt') || '',
        };
      }
    }

    // find video poster
    const $video = $cell.querySelector('video');
    if ($video) {
      const poster = $video.poster || $video.getAttribute('poster');
      if (poster) {
        return {
          src: poster,
          srcset: '',
          alt: $video.getAttribute('aria-label') || $video.getAttribute('alt') || '',
        };
      }
    }

    // Try to find background image from style
    const $elementWithBg = $cell.querySelector('[style*="background-image"]');
    if ($elementWithBg) {
      const bgImage = $elementWithBg.style.backgroundImage;
      const match = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
      if (match && match[1]) {
        return {
          src: match[1],
          srcset: '',
          alt: '',
        };
      }
    }

    return null;
  }

  /**
   * Check if cell is an artwork cell
   */
  function isArtworkCell($cell) {
    return !!(
      $cell.classList.contains('js-post-artwork-image') ||
      $cell.querySelector('.pv-gallery__item-upload--artwork') ||
      $cell.querySelector('.pv-gallery__item-upload--artwork-loading') ||
      $cell.querySelector('svg.artwork')
    );
  }

  /**
   * Create custom navigation buttons
   * https://flickity.metafizzy.co/extras
   * @description creates custom navigation buttons for the carousel
   */
  function createCustomNavigation($carousel, $gallery) {
    const $cells = $carousel.querySelectorAll(c.flickityOpts.cellSelector);
    const cellCount = $cells.length;

    if (cellCount <= 1) return;

    let $navContainer = $gallery?.querySelector('.js-product-gallery-nav') || null;

    if (!$navContainer && $gallery) {
      $navContainer = document.createElement('div');
      $navContainer.className = 'js-product-gallery-nav pv-gallery__nav';
      $gallery.appendChild($navContainer);
    } else if (!$navContainer) {
      $navContainer = document.createElement('div');
      $navContainer.className = 'js-product-gallery-nav pv-gallery__nav';
      $carousel.parentNode.insertBefore($navContainer, $carousel.nextSibling);
    }

    $navContainer.innerHTML = '';

    for (let i = 0; i < cellCount; i++) {
      const $cell = $cells[i];
      const imageData = getImageFromCell($cell);
      const isArtwork = isArtworkCell($cell);

      const $button = document.createElement('button');
      $button.type = 'button';
      $button.className = `pv-gallery__nav-button ${c.cls.navButton}`;
      $button.setAttribute('data-nav-index', i);
      $button.setAttribute('aria-label', `Go to slide ${i + 1}`);
      if (imageData && imageData.src) {
        if (isArtwork) {
          const $originalArtworkElement = $cell.querySelector(`.${c.dom.post_artwork}`);
          
          if ($originalArtworkElement) {
            const $artworkElement = document.createElement('div');
            const originalId = $originalArtworkElement.id || '';
            const navId = originalId ? `nav-${originalId}-${i}` : `nav-postArtworkImage-${i}`;
            
            $artworkElement.id = navId;
            $artworkElement.className = 'pv-gallery__item-upload is-loading js-post-artwork-image';
            
            // Copy all data attributes from original element
            Object.keys($originalArtworkElement.dataset).forEach((key) => {
              const value = $originalArtworkElement.dataset[key];
              if (value) {
                // Convert camelCase to kebab-case for data attributes
                const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                $artworkElement.setAttribute(`data-${kebabKey}`, value);
              }
            });
            
            const dataAttrMap = {
              'framespec-eligible': 'framespecEligible',
              'product-id': 'productId',
              'variant-spec': 'variantSpec'
            };
            
            Object.keys(dataAttrMap).forEach((attrName) => {
              const camelKey = dataAttrMap[attrName];
              if (!$artworkElement.hasAttribute(`data-${attrName}`)) {
                const value = $originalArtworkElement.getAttribute(`data-${attrName}`) || 
                            $originalArtworkElement.dataset[camelKey];
                if (value) {
                  $artworkElement.setAttribute(`data-${attrName}`, value);
                }
              }
            });
            
            $button.appendChild($artworkElement);
            
            setTimeout(() => {
              postArtwork();
            }, 0);
          } else {
            // Fallback: use image thumbnail if original element not found
            const $thumbnail = document.createElement('img');
            $thumbnail.src = imageData.src;
            if (imageData.srcset) {
              $thumbnail.srcset = imageData.srcset;
            }
            $thumbnail.alt = imageData.alt || `Thumbnail ${i + 1}`;
            $thumbnail.className = 'pv-gallery__nav-thumbnail';
            $thumbnail.loading = 'lazy';
            $button.appendChild($thumbnail);
          }
        } else {
          // Regular image thumbnail
          const $thumbnail = document.createElement('img');
          $thumbnail.src = imageData.src;
          if (imageData.srcset) {
            $thumbnail.srcset = imageData.srcset;
          }
          $thumbnail.alt = imageData.alt || `Thumbnail ${i + 1}`;
          $thumbnail.className = 'pv-gallery__nav-thumbnail';
          $thumbnail.loading = 'lazy';
          
          $thumbnail.onerror = () => {
            // If image fails to load, show number as fallback
            $thumbnail.style.display = 'none';
            if (!$button.querySelector('.pv-gallery__nav-fallback')) {
              const $fallback = document.createElement('span');
              $fallback.className = 'pv-gallery__nav-fallback';
              $fallback.textContent = i + 1;
              $button.appendChild($fallback);
            }
          };
          
          $button.appendChild($thumbnail);
        }
      } else {
        if (isArtwork) {
          const $originalArtworkElement = $cell.querySelector(`.${c.dom.post_artwork}`);
          
          if ($originalArtworkElement) {
            // Create new artwork element with same structure as Liquid template
            const $artworkElement = document.createElement('div');
            const originalId = $originalArtworkElement.id || '';
            const navId = originalId ? `nav-${originalId}-${i}` : `nav-postArtworkImage-${i}`;
            
            $artworkElement.id = navId;
            $artworkElement.className = 'pv-gallery__item-upload is-loading js-post-artwork-image';
            
            const dataAttributes = [
              'framespecEligible',
              'handle',
              'type',
              'productId',
              'variant',
              'variantSpec'
            ];
            
            dataAttributes.forEach((attr) => {
              const value = $originalArtworkElement.dataset[attr] || $originalArtworkElement.getAttribute(`data-${attr.toLowerCase().replace(/([A-Z])/g, '-$1').toLowerCase()}`);
              if (value) {
                const dataAttrName = attr === 'framespecEligible' ? 'framespec-eligible' : 
                                   attr === 'productId' ? 'product-id' :
                                   attr === 'variantSpec' ? 'variant-spec' :
                                   attr.toLowerCase();
                $artworkElement.setAttribute(`data-${dataAttrName}`, value);
              }
            });
            
            $button.appendChild($artworkElement);
            
            setTimeout(() => {
              postArtwork();
            }, 0);
          } else {
            const $fallback = document.createElement('span');
            $fallback.className = 'pv-gallery__nav-fallback';
            $fallback.textContent = i + 1;
            $button.appendChild($fallback);
          }
        } else {
          const $fallback = document.createElement('span');
          $fallback.className = 'pv-gallery__nav-fallback';
          $fallback.textContent = i + 1;
          $button.appendChild($fallback);
        }
      }

      if (i === 0) {
        $button.classList.add(c.cls.navButtonActive);
      }

      $navContainer.appendChild($button);
    }
  }

  /**
   * Update navigation buttons active state
   */
  function updateNavigation($carousel, selectedIndex) {
    const $gallery = __.parents($carousel, c.dom.gallery)[0] || $carousel.parentNode;
    const $navContainer = $gallery?.querySelector('.js-product-gallery-nav') || $carousel.parentNode.querySelector('.js-product-gallery-nav');
    
    if (!$navContainer) return;

    const $navButtons = $navContainer.querySelectorAll(`.${c.cls.navButton}`);
    $navButtons.forEach(($button, idx) => {
      if (idx === parseInt(selectedIndex, 10)) {
        $button.classList.add(c.cls.navButtonActive);
      } else {
        $button.classList.remove(c.cls.navButtonActive);
      }
    });
  }

  /**
   * Make carousel
   */
  function makeCarousel($carousel, destroyCarousel = false) {
    if (destroyCarousel) {
      const prev = Flickity.data($carousel);
      if (prev) {
        prev.destroy();
      }
    }

    const $gallery = __.parents($carousel, c.dom.gallery)[0] || false;
    createCustomNavigation($carousel, $gallery);

    const flickityCarousel = new Flickity($carousel, c.flickityOpts);
    
    flickityCarousel.on('change', () => {
      const selectedIndex = $carousel.querySelector(c.cls.selected)?.dataset?.cellIndex;
      $carousel.nextElementSibling?.setAttribute('data-cell-index', selectedIndex);
      
      updateNavigation($carousel, selectedIndex);

      const $videoBtns = [...$carousel.querySelectorAll(c.dom.videoBtn)];
      if ($videoBtns && $videoBtns.length > 0) {
        $videoBtns.forEach(($videoBtn) => {
          $videoBtn.setAttribute('tabindex', -1);
        });
      }
      const $selectedVideoBtns = [...$carousel.querySelectorAll(`[data-cell-index="${selectedIndex}"] ${c.dom.videoBtn}`)];
      if ($selectedVideoBtns && $selectedVideoBtns.length > 0) {
        $selectedVideoBtns.forEach(($selectedVideoBtn) => {
          $selectedVideoBtn.setAttribute('tabindex', 0);
        });
      }
    });

    // Add click handlers to navigation buttons
    const $galleryForNav = __.parents($carousel, c.dom.gallery)[0] || $carousel.parentNode;
    const $navContainer = $galleryForNav?.querySelector('.js-product-gallery-nav') || $carousel.parentNode.querySelector('.js-product-gallery-nav');
    
    if ($navContainer) {
      const $navButtons = $navContainer.querySelectorAll(`.${c.cls.navButton}`);
      $navButtons.forEach(($button) => {
        $button.addEventListener('click', () => {
          const navIndex = parseInt($button.getAttribute('data-nav-index'), 10);
          flickityCarousel.select(navIndex);
        });
      });
    }
  }

  return {
    init,
    makeCarousel,
    arrangeImages,
  };
}

export default productGalleryCarousel;
